import type { StatusChipColor } from "components/StatusChip";

import { isEnforcedSesVerdict, type SesVerdict } from "encamp-shared/src/emailIntake";
import {
  EmailIntakeSenderMatchType,
  ExtractionStatus,
  ReceivedEmailAttachmentCode,
  ReceivedEmailActionKind,
  ReceivedEmailAttention,
  ReceivedEmailStatus,
  ReceivedEmailRejectionReason,
} from "generated-graphql/graphql";
import { prettyPrintDateTimeMed, relativeLogTime } from "util/dates";

import type { InboundEmailRow, SenderRow } from "./senderDialogValues";

/**
 * The tab's display logic, kept out of the components so it can be tested directly.
 *
 * These are the strings a reader actually acts on — whether a sender is still in use,
 * whether an email was filed or refused and why — and every one of them has a wrong
 * answer that looks plausible. One already shipped: an entry with mail but no
 * documents read "No mail yet".
 */

/**
 * Why an email was refused, short enough to sit in a chip.
 *
 * Terse on purpose: a sentence-long label made the chip wider than its column, and
 * the cell clipped it. Every reason carries the full explanation in REJECTION_HELP,
 * which is where the detail belongs — the chip only has to be recognisable at a
 * glance. Still never the enum name, which says what the code decided rather than
 * what to do about it.
 */
export const REJECTION_LABELS: Record<ReceivedEmailRejectionReason, string> = {
  [ReceivedEmailRejectionReason.SenderNotAllowed]: "Not allowlisted",
  [ReceivedEmailRejectionReason.SenderDisabled]: "Switched off",
  [ReceivedEmailRejectionReason.RecipientMismatch]: "Wrong address",
  [ReceivedEmailRejectionReason.SesVerdictFailed]: "Failed checks",
  [ReceivedEmailRejectionReason.MalformedFrom]: "Unreadable sender",
};

/** Every reason has one, since the labels above no longer explain themselves. */
export const REJECTION_HELP: Record<ReceivedEmailRejectionReason, string> = {
  [ReceivedEmailRejectionReason.SenderNotAllowed]:
    "No allowlist entry matched this address or its domain, so the email was refused without a reply.",
  [ReceivedEmailRejectionReason.SenderDisabled]:
    "An allowlist entry matched but is turned off. Mail is refused rather than routed to whoever holds a broader entry.",
  [ReceivedEmailRejectionReason.RecipientMismatch]:
    "Delivered to an address that is not this environment's manifest intake address.",
  [ReceivedEmailRejectionReason.SesVerdictFailed]:
    "The SES spam, virus or DMARC verdicts did not all pass, so the message was refused before it was opened. DMARC is the authenticity check; SPF and DKIM are recorded but do not refuse on their own.",
  [ReceivedEmailRejectionReason.MalformedFrom]:
    "The From header was absent, repeated, or could not be parsed, so there was no sender to match against.",
};

/**
 * "4 days ago" close in, a timestamp once that stops being useful.
 *
 * Delegates to the app's `relativeLogTime`, whose default cutoff is the same seven
 * days this used to hardcode with date-fns. The question it answers is "is this entry
 * still live, or config nobody has cleaned up" — recency matters more than the exact
 * moment, which the tooltip carries.
 */
export function relativeReceived(value: string | null | undefined): string {
  if (!value) return "Never";
  return relativeLogTime(value);
}

/**
 * One chip's worth of text for how an entry matches.
 *
 * Subdomain scope is part of what the match *is*, not a footnote about it, so it
 * reads on the same line — a domain that accepts subdomains and one that does not
 * are two different grants, and splitting them across two lines made the narrower
 * one look like the default with an annotation.
 */
export function matchLabel(row: SenderRow): string {
  if (row.matchType === EmailIntakeSenderMatchType.Address) return "Address";
  return row.includeSubdomains ? "Domain + subdomains" : "Domain";
}

/**
 * When a message arrived. The app's medium datetime, rendered in the reader's own
 * timezone — "Aug 28, 2026, 8:33 AM".
 *
 * This was a hand-rolled "Today 8:33 AM" / "Aug 28 · 8:33 AM", which read nicely but
 * was a fourth date format on a page that already had three, in a different library
 * from every other date in the app.
 */
export function receivedLabel(value: string): string {
  return prettyPrintDateTimeMed(value);
}

/**
 * "2 of 2 accepted", "None" when nothing was attached, or why the count is unknown.
 *
 * Mail that is waiting on a person — held, whether from an Encamp sender or an ambiguous
 * one — has its
 * attachments listed but nothing accepted yet, and "0 of 2 accepted" would read as
 * two failures. Those say what is there instead.
 *
 * A missing count is not zero. Mail refused unopened — an unknown or switched-off
 * sender — may well have carried manifests; a dash there would claim it did not.
 */
/** Held mail lists what arrived without filing any of it. */
function isHeldStatus(status: ReceivedEmailStatus): boolean {
  return (
    status === ReceivedEmailStatus.HeldEncampSender || status === ReceivedEmailStatus.HeldAmbiguous
  );
}

export function attachmentsLabel(row: InboundEmailRow): string {
  const total = row.attachmentsTotalCount;
  if (total == null) return unknownAttachmentsLabel(row.status);
  if (total === 0) return "None";
  if (isHeldStatus(row.status) || row.status === ReceivedEmailStatus.FailedValidation) {
    return `${total} attached`;
  }
  return `${row.attachmentsProcessedCount ?? 0} of ${total} accepted`;
}

function unknownAttachmentsLabel(status: ReceivedEmailStatus): string {
  switch (status) {
    case ReceivedEmailStatus.FailedValidation:
      return "Not examined";
    case ReceivedEmailStatus.Processing:
      return "—";
    default:
      return "Not recorded";
  }
}

/**
 * Why one attachment was refused, or the note carried by an accepted one.
 *
 * Written for the person reading the staff panel, not the sender: the reply already
 * explains how to fix it, and this only has to say what happened.
 */
export const ATTACHMENT_CODE_LABELS: Record<ReceivedEmailAttachmentCode, string> = {
  [ReceivedEmailAttachmentCode.NoFilename]: "No filename",
  [ReceivedEmailAttachmentCode.NotPdf]: "Not a PDF",
  [ReceivedEmailAttachmentCode.FiletypeMismatch]: "Extension says PDF, contents do not",
  [ReceivedEmailAttachmentCode.DuplicateName]: "Another attachment had this filename",
  [ReceivedEmailAttachmentCode.DuplicateFile]: "Identical copy attached more than once",
  // Ours, not the sender's. Worth saying plainly so nobody asks them to resend.
  [ReceivedEmailAttachmentCode.InternalServerError]: "We failed to process it",
};

/**
 * What the status chip says, and how loudly.
 *
 * Reads the stored `status` rather than deriving a label from the two counts. The
 * derivation used to live here, which meant the label existed only in the
 * browser: the grid pages server-side, so "show me every partial delivery" could not
 * be asked. It is a column now, and this only has to name it.
 *
 * Colour is a severity ladder, and nothing that failed to land is grey: green filed,
 * blue still in flight, amber something did not arrive, red nothing arrived or we
 * broke. A refusal is the system working correctly, but from the reader's side it is
 * still a manifest that did not reach the customer — drawing eleven of them in grey
 * said "nothing to see here" about the rows this page exists to surface.
 *
 * Red stays for the two total losses, so a routine refusal still does not read like an
 * outage.
 */
export function statusDisplay(row: InboundEmailRow): {
  color: StatusChipColor;
  label: string;
  help?: string;
} {
  switch (row.status) {
    case ReceivedEmailStatus.Filed:
      return { color: "success", label: "Filed" };

    case ReceivedEmailStatus.Partial: {
      const total = row.attachmentsTotalCount ?? 0;
      const failed = total - (row.attachmentsProcessedCount ?? 0);
      return {
        color: "warning",
        label: `Partial · ${failed} failed`,
        help: `${failed} of ${total} attachments could not be filed. Expand the row to see which, and why.`,
      };
    }

    case ReceivedEmailStatus.NothingFiled:
      return {
        color: "error",
        label: "Nothing filed",
        help: "Attachments arrived and none could be filed. The sender was told which failed and why.",
      };

    // Previously reported as a success: the sender was told nothing had been done
    // while this read "Filed".
    // Not always answered: a vendor is told, but staff mail with nothing attached is
    // closed quietly rather than treated as a routing item, so the help does not
    // claim a reply that one of the two paths never sends.
    case ReceivedEmailStatus.NoAttachments:
      return {
        color: "warning",
        label: "No attachments",
        help: "The message arrived with nothing attached, so there was nothing to file.",
      };

    case ReceivedEmailStatus.FailedValidation: {
      const reason = row.rejectionReason;
      return {
        color: "warning",
        label: reason ? REJECTION_LABELS[reason] : "Refused",
        help: reason ? REJECTION_HELP[reason] : undefined,
      };
    }

    case ReceivedEmailStatus.Errored:
      return {
        color: "error",
        label: "Errored",
        help: "Processing failed unexpectedly after the sender was accepted. Engineering is alerted to these.",
      };

    // Neither a success nor a failure: a person has to choose. Info, so it neither
    // blends in with filed mail nor reads as something broken.
    case ReceivedEmailStatus.HeldEncampSender:
      return {
        color: "info",
        label: "Held — Encamp sender",
        help: "Sent from an Encamp address that is not allowlisted for any organization. Nothing has been filed, and there is no allowlist entry to fix, so choose the organization from the row's actions.",
      };

    case ReceivedEmailStatus.HeldAmbiguous:
      return {
        color: "info",
        label: "Held — ambiguous sender",
        help: "More than one organization allowlists this sender, so the allowlist cannot decide. Remove or switch off the losing entry and replay, and their next email routes on its own. Route it by hand instead when both entries are correct.",
      };

    case ReceivedEmailStatus.Processing:
      return { color: "info", label: "Processing" };
  }
  // Every status has a case above. A new one is a compile error here rather than a
  // chip that silently reads "Processing".
  const exhaustive: never = row.status;
  return exhaustive;
}

/** How a row's attention reads. Dismissed is a staff verdict, not a status. */
export function attentionDisplay(row: {
  attention: ReceivedEmailAttention;
  dismissedAt?: string | null;
  dismissedBy?: string | null;
}): { color: StatusChipColor; label: string; help?: string } | null {
  if (row.attention === ReceivedEmailAttention.Dismissed) {
    const who = row.dismissedBy ? ` by ${row.dismissedBy}` : "";
    const when = row.dismissedAt ? ` on ${prettyPrintDateTimeMed(row.dismissedAt)}` : "";
    return {
      color: "default",
      label: "Dismissed",
      help: `Marked as needing no action${who}${when}. What happened to the email is unchanged.`,
    };
  }
  if (row.attention === ReceivedEmailAttention.Needed) {
    return { color: "warning", label: "Needs attention" };
  }
  return null;
}

/** Whether one action is offered, from the list the API computed. */
export function actionState(
  row: {
    actions: ReadonlyArray<{
      kind: ReceivedEmailActionKind;
      available: boolean;
      blockedReason?: string | null;
    }>;
  },
  kind: ReceivedEmailActionKind
): { available: boolean; blockedReason: string | null } {
  const action = row.actions.find((candidate) => candidate.kind === kind);
  if (!action) return { available: false, blockedReason: null };
  return { available: action.available, blockedReason: action.blockedReason ?? null };
}

/**
 * What to say about the acknowledgement when there is no reply row.
 *
 * Two different facts wear the same empty value. We deliberately never answer a
 * sender we could not place, so for refused mail "none" is the system working. For
 * mail we did place, "none" means only that no reply was recorded — the message may
 * predate reply tracking — and claiming the sender was unplaceable would be false
 * about an email that reached a customer.
 */
export function missingReplyText(status: ReceivedEmailStatus, attributed: boolean): string {
  if (status === ReceivedEmailStatus.HeldEncampSender) {
    return "None — the forwarder was not notified; sending the routing notice failed";
  }
  if (status === ReceivedEmailStatus.FailedValidation || !attributed) {
    return "None — we never answer a sender we could not place";
  }
  return "Not recorded";
}

/**
 * Why the per-attachment list is empty, when the counts say there were attachments.
 *
 * Silence here would reproduce the problem the list exists to solve: a reader sees
 * "3 of 4 accepted" and nothing about the one that failed. Messages received before
 * this was recorded genuinely have no detail, and saying so is the difference between
 * "we did not keep it" and a page that looks broken.
 */
export function missingAttachmentDetailText(total: number | null | undefined): string | null {
  if (!total || total === 0) return null;
  return "Per-attachment detail was not recorded for this message.";
}

/**
 * The exact moment, for a tooltip behind a relative label.
 *
 * "4 days ago" answers "is this entry still in use" faster, but someone correlating
 * against a customer's report needs the timestamp itself.
 */
export function exactTimestamp(value: string | null | undefined): string {
  if (!value) return "Never";
  return prettyPrintDateTimeMed(value);
}

/**
 * "Dallas Depot · Dallas, TX", or null when the entry carries no facility hint.
 * City and state are appended only when both are known — a lone ", TX" reads as a
 * typo.
 */
export function facilityHintLabel(
  facility: { name: string; city?: string | null; state?: string | null } | null | undefined
): string | null {
  if (!facility) return null;
  const place = facility.city && facility.state ? ` · ${facility.city}, ${facility.state}` : "";
  return `${facility.name}${place}`;
}

/**
 * Why the "Documents created" list is empty. Four different facts wear that one
 * empty list, and the first version collapsed them into two — reporting "the message
 * carried no attachments" for a refusal whose attachments were never opened, which
 * is a guess presented as fact.
 */
export function noDocumentsText(row: {
  status: ReceivedEmailStatus;
  attachmentsTotalCount?: number | null;
}): string {
  const total = row.attachmentsTotalCount;
  if (total == null) {
    if (row.status === ReceivedEmailStatus.FailedValidation) {
      return "The message was refused before its attachments were examined.";
    }
    return "Attachment detail was not recorded for this message.";
  }
  if (total === 0) return "The message carried no attachments.";
  if (isHeldStatus(row.status) || row.status === ReceivedEmailStatus.FailedValidation) {
    return "Nothing has been filed yet; the attachments are listed above.";
  }
  return "The attachments were refused, or processing did not finish.";
}

/**
 * Why an unenforced verdict reads the way it does.
 *
 * Copy, so it lives with the surface that shows it rather than in the shared rules the
 * lambda reads. Which verdicts are enforced is a fact both sides need; the sentence
 * explaining one of them to a staff member is not.
 */
const UNENFORCED_SES_VERDICT_HELP: Partial<Record<SesVerdict, string>> = {
  spf: "Recorded, not enforced. DMARC passes on either SPF or DKIM, so a message that proves itself by DKIM alone is legitimate with SPF failing. DMARC is the check that decides.",
  dkim: "Recorded, not enforced. Microsoft 365 signs in Microsoft's name rather than the sender's own by default, so DKIM never aligns for most corporate senders, however genuine the mail is. DMARC passes on either SPF or DKIM, and is the check that decides.",
};

/**
 * What a failing DMARC verdict means. GRAY especially, which reads like a near-miss
 * but means the domain publishes no DMARC record at all.
 */
const DMARC_VERDICT_HELP: Record<string, string> = {
  GRAY: "The sending domain publishes no DMARC record, so there is nothing to prove the message is really from them. Refused. Allowlisting cannot fix this — the sender has to publish DMARC, or send from a domain that does.",
  FAIL: "The message did not prove it came from the domain in its From address. Refused.",
};

export type VerdictDisplay = { color: StatusChipColor; label: string; help: string | null };

/**
 * One SES verdict as a chip. A PASS is the boring case; only a failure that intake
 * would act on earns colour. A verdict intake does not enforce is never red, whatever
 * it says — a red FAIL on every production row would be noise, and reading it as a
 * refusal would be wrong — and it carries the reason it is shown at all.
 */
export function verdictDisplay(verdict: SesVerdict, value: string): VerdictDisplay {
  const name = verdict.toUpperCase();
  const status = value.toUpperCase();
  if (!isEnforcedSesVerdict(verdict)) {
    return {
      color: "default",
      label: `${name} ${status} (not enforced)`,
      help: UNENFORCED_SES_VERDICT_HELP[verdict] ?? null,
    };
  }
  if (status === "PASS") {
    return { color: "default", label: `${name} ${status}`, help: null };
  }
  return {
    color: "error",
    label: `${name} ${status}`,
    help: verdict === "dmarc" ? (DMARC_VERDICT_HELP[status] ?? null) : null,
  };
}

/**
 * Where a filed document lives in the customer's app, or null when the email resolved
 * to no organization and there is no customer app to point at.
 *
 * The omnisearch term selects the document on arrival, so the reader lands on it
 * rather than on a list of everything.
 */
export function customerDocumentHref(
  tenantId: string | null | undefined,
  documentId: string
): string | null {
  if (!tenantId) return null;
  return `/o/${tenantId}/documents/${documentId}?omnisearch=${encodeURIComponent(`id:${documentId}`)}`;
}

/** The manifest page in the customer's waste section — where the trail from an email ends. */
export function customerManifestHref(
  tenantId: string | null | undefined,
  processedManifestId: string
): string | null {
  if (!tenantId) return null;
  return `/o/${tenantId}/waste/manifests/${processedManifestId}`;
}

/** What the email page knows about a filed document's life after filing. */
export type DocumentFate = {
  extractionStatus?: ExtractionStatus | null;
  needsReview?: boolean | null;
  reviewedAt?: string | null;
  suggestedFacility?: { name: string } | null;
  manifestFacility?: { name: string } | null;
};

/**
 * One line on what became of a filed attachment, and how worried to be about it.
 *
 * "Filed" was where the email page's story used to stop, and it is the least
 * interesting fact: a filed document that never parsed, or parsed and has waited two
 * weeks for a reviewer, or parsed to a facility nobody has confirmed, is the thing a
 * person here is actually asking about. Warning for anything that is waiting on
 * someone or something; error only for a parse that failed outright.
 */
export function documentFateLabel(doc: DocumentFate): {
  text: string;
  tone: "neutral" | "warning" | "error";
} {
  switch (doc.extractionStatus) {
    case ExtractionStatus.Failed:
      return { text: "Parse failed", tone: "error" };
    case ExtractionStatus.Pending:
      return { text: "Parsing", tone: "neutral" };
    case ExtractionStatus.Succeeded:
      break;
    default:
      return { text: "Not parsed yet", tone: "warning" };
  }

  const facility = doc.manifestFacility?.name ?? doc.suggestedFacility?.name;
  // A facility the parser bound on its own is still a suggestion until a person agrees.
  const suggested = Boolean(doc.needsReview && !doc.manifestFacility);
  let facilityPart = "no facility matched";
  if (facility) {
    facilityPart = suggested ? `${facility} (suggested)` : facility;
  }

  if (doc.needsReview) {
    return { text: `Awaiting review · ${facilityPart}`, tone: "warning" };
  }
  const when = doc.reviewedAt ? ` ${prettyPrintDateTimeMed(doc.reviewedAt)}` : "";
  return { text: `Reviewed${when} · ${facilityPart}`, tone: "neutral" };
}

/**
 * A size a person reads at a glance — "1.2 MB", "340 KB" — or null when it was never
 * recorded, so the caller can omit it rather than print "0 B" for an unknown.
 */
export function formatBytes(bytes: number | null | undefined): string | null {
  if (bytes == null || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rendered = value >= 10 ? Math.round(value).toString() : value.toFixed(1);
  return `${rendered} ${units[unit]}`;
}

/**
 * What was declared and how big it was, for the attachment list — or null when the
 * row predates both being recorded, so nothing renders rather than a stray "·".
 */
export function attachmentMetaLabel(attachment: {
  mimeType?: string | null;
  sizeBytes?: number | null;
}): string | null {
  const parts = [attachment.mimeType, formatBytes(attachment.sizeBytes)].filter(
    (part): part is string => Boolean(part)
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * What staff last did to this email, in one line, or null when nobody has re-run it.
 *
 * A route is a replay with the organization chosen by hand, and the row shows it as
 * such: attributed to an organization with no allowlist entry credited. The two
 * timestamps are written together, so a row with one and not the other is a row this
 * code should not guess about.
 */
export function historyLine(row: {
  status: ReceivedEmailStatus;
  lastReplayedAt?: string | null;
  lastReplayedBy?: string | null;
  tenant?: { name: string } | null;
  matchedSender?: unknown | null;
}): string | null {
  if (!row.lastReplayedAt || !row.lastReplayedBy) return null;
  // Which of the two this was is read from the organization and the matched entry,
  // and neither is written until the run finishes. Saying nothing for those seconds
  // beats calling a route a replay.
  if (row.status === ReceivedEmailStatus.Processing) return null;
  const when = prettyPrintDateTimeMed(row.lastReplayedAt);
  if (row.tenant && !row.matchedSender) {
    return `Routed to ${row.tenant.name} by ${row.lastReplayedBy} · ${when}`;
  }
  return `Replayed by ${row.lastReplayedBy} · ${when}`;
}
